<template>
  <el-dialog
    class="user-feedback-dialog-wrapper"
    :visible.sync="dialogVisible"
    width="1120px"
    top="4vh"
    custom-class="user-feedback-dialog"
    :close-on-click-modal="false"
    @opened="handleOpened">
    <div slot="title" class="dialog-heading">
      <span class="dialog-heading__icon"><i class="el-icon-chat-dot-square"></i></span>
      <div><strong>提交反馈</strong><small>你的意见会帮助我们持续改进产品</small></div>
    </div>
    <div class="feedback-page">
    <section class="feedback-hero">
      <div>
        <h1>告诉我们，哪里还可以做得更好</h1>
        <p>功能建议、使用疑问或故障问题都可以在这里提交。我们会记录每一条反馈，并在处理后同步进展。</p>
      </div>
      <div class="hero-note">
        <i class="el-icon-time"></i>
        <div><strong>反馈可追踪</strong><span>提交后可在右侧随时查看状态与回复</span></div>
      </div>
    </section>

    <div class="feedback-workspace">
      <section class="panel submit-panel">
        <div class="panel-heading">
          <div><h2>提交新反馈</h2></div>
          <span class="required-tip">带 * 为必填项</span>
        </div>

        <el-form ref="feedbackForm" :model="form" :rules="rules" label-position="top">
          <el-form-item label="反馈类型" prop="feedbackType">
            <div class="type-grid">
              <button
                v-for="item in feedbackTypes"
                :key="item.value"
                type="button"
                class="type-option"
                :class="{'is-active': form.feedbackType === item.value}"
                @click="form.feedbackType = item.value">
                <i :class="item.icon"></i>
                <span><strong>{{ item.label }}</strong><small>{{ item.description }}</small></span>
                <i v-if="form.feedbackType === item.value" class="el-icon-success selected-icon"></i>
              </button>
            </div>
          </el-form-item>

          <el-form-item label="反馈标题" prop="title">
            <el-input
              v-model="form.title"
              maxlength="100"
              show-word-limit
              clearable
              placeholder="用一句话概括你的反馈" />
          </el-form-item>

          <el-form-item label="详细说明" prop="content">
            <el-input
              v-model="form.content"
              type="textarea"
              :rows="7"
              maxlength="2000"
              show-word-limit
              resize="none"
              placeholder="请描述操作过程、实际结果和期望结果。" />
          </el-form-item>

          <div class="submit-guidance">
            <i class="el-icon-info"></i>
            <span>请勿填写账号密码、令牌等敏感信息。</span>
          </div>
          <el-button
            class="submit-button"
            type="primary"
            :loading="submitting"
            @click="submitFeedback">
            <i v-if="!submitting" class="el-icon-s-promotion"></i>
            {{ submitting ? '正在提交' : '提交反馈' }}
          </el-button>
        </el-form>
      </section>

      <section class="panel history-panel">
        <div class="panel-heading history-heading">
          <div><h2>我的反馈</h2></div>
          <el-button class="refresh-button" icon="el-icon-refresh" circle :loading="loading" @click="loadFeedbacks" />
        </div>

        <el-radio-group v-model="statusFilter" class="status-filter" size="small" @change="changeStatus">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button :label="0">待处理</el-radio-button>
          <el-radio-button :label="1">处理中</el-radio-button>
          <el-radio-button :label="2">已处理</el-radio-button>
        </el-radio-group>

        <div v-loading="loading" class="feedback-list">
          <button
            v-for="item in feedbackList"
            :key="item.id"
            type="button"
            class="feedback-card"
            @click="showDetail(item.id)">
            <div class="card-topline">
              <span class="type-label"><i :class="feedbackTypeMeta(item.feedbackType).icon"></i>{{ feedbackTypeMeta(item.feedbackType).label }}</span>
              <span class="status-badge" :class="'status-' + item.status"><i></i>{{ statusText(item.status) }}</span>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.content }}</p>
            <div v-if="item.latestReply" class="latest-reply"><i class="el-icon-chat-line-square"></i><span>{{ item.latestReply }}</span></div>
            <div class="card-footer"><span>{{ item.createTime }}</span><span>查看详情 <i class="el-icon-arrow-right"></i></span></div>
          </button>

          <div v-if="!loading && feedbackList.length === 0" class="empty-state">
            <div class="empty-icon"><i class="el-icon-chat-line-square"></i></div>
            <strong>{{ statusFilter === '' ? '还没有提交过反馈' : '当前状态下没有反馈' }}</strong>
            <span>{{ statusFilter === '' ? '你的意见会帮助我们持续改进产品' : '切换状态看看其他反馈记录' }}</span>
          </div>
        </div>

        <el-pagination
          v-if="totalCount > pageSize"
          class="feedback-pagination"
          background
          layout="prev, pager, next"
          :current-page="pageNum"
          :page-size="pageSize"
          :total="totalCount"
          @current-change="changePage" />
      </section>
    </div>

    <transition name="detail-slide">
      <div v-if="detailVisible" class="detail-overlay">
        <div class="detail-overlay__header">
          <div class="detail-title">
        <span>反馈详情</span>
          </div>
          <button type="button" aria-label="关闭反馈详情" @click="detailVisible = false"><i class="el-icon-close"></i></button>
        </div>
        <div v-loading="detailLoading" class="detail-content">
        <template v-if="detail.id">
          <div class="detail-summary">
            <div class="detail-meta">
              <span class="type-label"><i :class="feedbackTypeMeta(detail.feedbackType).icon"></i>{{ feedbackTypeMeta(detail.feedbackType).label }}</span>
              <span class="status-badge" :class="'status-' + detail.status"><i></i>{{ statusText(detail.status) }}</span>
            </div>
            <h2>{{ detail.title }}</h2>
            <p>{{ detail.content }}</p>
          </div>
          <div class="progress-title">处理进度</div>
          <div class="timeline">
            <div class="timeline-item">
              <span class="timeline-dot submitted"><i class="el-icon-check"></i></span>
              <div><strong>反馈已提交</strong><time>{{ detail.createTime }}</time><p>我们已经收到你的反馈。</p></div>
            </div>
            <div v-for="record in detail.records" :key="record.id" class="timeline-item">
              <span class="timeline-dot" :class="record.status === 2 ? 'resolved' : 'processing'"><i :class="record.status === 2 ? 'el-icon-check' : 'el-icon-more'"></i></span>
              <div>
                <strong>{{ statusText(record.status) }}</strong><time>{{ record.createTime }}</time>
                <p v-if="record.replyContent" class="record-reply">{{ record.replyContent }}</p>
                <p v-else>反馈状态已更新。</p>
              </div>
            </div>
            <div v-if="detail.records && detail.records.length === 0" class="timeline-item pending-item">
              <span class="timeline-dot waiting"><i class="el-icon-time"></i></span>
              <div><strong>等待处理</strong><p>工作人员受理后，处理进展会显示在这里。</p></div>
            </div>
          </div>
        </template>
        </div>
      </div>
    </transition>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'UserFeedback',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      feedbackTypes: [
        { value: 1, label: '功能建议', description: '期待的新功能或改进', icon: 'el-icon-magic-stick' },
        { value: 2, label: '使用问题', description: '操作或配置方面的疑问', icon: 'el-icon-question' },
        { value: 3, label: '故障 / Bug', description: '异常、报错或数据问题', icon: 'el-icon-warning-outline' },
        { value: 9, label: '其他反馈', description: '其他想告诉我们的内容', icon: 'el-icon-more-outline' }
      ],
      form: {
        feedbackType: null,
        title: '',
        content: ''
      },
      rules: {
        feedbackType: [{ required: true, message: '请选择反馈类型', trigger: 'change' }],
        title: [{ required: true, whitespace: true, message: '请输入反馈标题', trigger: 'blur' }],
        content: [{ required: true, whitespace: true, message: '请输入详细说明', trigger: 'blur' }]
      },
      submitting: false,
      loading: false,
      statusFilter: '',
      feedbackList: [],
      pageNum: 1,
      pageSize: 6,
      totalCount: 0,
      detailVisible: false,
      detailLoading: false,
      detail: {}
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) this.detailVisible = false;
        this.$emit('update:visible', value);
      }
    }
  },
  methods: {
    handleOpened() {
      this.detailVisible = false;
      this.pageNum = 1;
      this.loadFeedbacks();
    },
    submitFeedback() {
      this.$refs.feedbackForm.validate(valid => {
        if (!valid || this.submitting) return;
        this.submitting = true;
        const payload = {
          feedbackType: this.form.feedbackType,
          title: this.form.title.trim(),
          content: this.form.content.trim()
        };
        this.$request.post('/api/feedback/submit', payload).then(response => {
          if (!response.data.result) {
            throw new Error(response.data.message || '提交反馈失败');
          }
          this.$message.success('反馈已提交，感谢你的建议');
          this.form = { feedbackType: null, title: '', content: '' };
          this.$nextTick(() => this.$refs.feedbackForm.clearValidate());
          this.statusFilter = '';
          this.pageNum = 1;
          this.loadFeedbacks();
        }).catch(error => {
          this.$message.error(this.errorMessage(error, '提交反馈失败，请稍后重试'));
        }).finally(() => {
          this.submitting = false;
        });
      });
    },
    loadFeedbacks() {
      this.loading = true;
      const payload = { pageNum: this.pageNum, pageSize: this.pageSize };
      if (this.statusFilter !== '') payload.status = this.statusFilter;
      this.$request.post('/api/feedback/page', payload).then(response => {
        if (!response.data.result) {
          throw new Error(response.data.message || '反馈记录加载失败');
        }
        const page = response.data.data || {};
        this.feedbackList = page.list || [];
        this.totalCount = Number(page.totalCount) || 0;
      }).catch(error => {
        this.feedbackList = [];
        this.totalCount = 0;
        this.$message.error(this.errorMessage(error, '反馈记录加载失败'));
      }).finally(() => {
        this.loading = false;
      });
    },
    changeStatus() {
      this.pageNum = 1;
      this.loadFeedbacks();
    },
    changePage(page) {
      this.pageNum = page;
      this.loadFeedbacks();
    },
    showDetail(id) {
      this.detail = {};
      this.detailVisible = true;
      this.detailLoading = true;
      this.$request.get('/api/feedback/' + id).then(response => {
        if (!response.data.result) {
          throw new Error(response.data.message || '反馈详情加载失败');
        }
        this.detail = response.data.data || {};
      }).catch(error => {
        this.detailVisible = false;
        this.$message.error(this.errorMessage(error, '反馈详情加载失败'));
      }).finally(() => {
        this.detailLoading = false;
      });
    },
    feedbackTypeMeta(type) {
      return this.feedbackTypes.find(item => item.value === type) || this.feedbackTypes[3];
    },
    statusText(status) {
      return ({ 0: '待处理', 1: '处理中', 2: '已处理' })[status] || '状态未知';
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : (error && error.message ? error.message : fallback);
    }
  }
};
</script>

<style scoped>
.feedback-page {
  position: relative;
  max-height: calc(92vh - 112px);
  padding: 4px 8px 10px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(91, 211, 235, 0.66) transparent;
  color: #dff7ff;
  background:
    radial-gradient(circle at 8% 4%, rgba(0, 204, 255, 0.12), transparent 27%),
    radial-gradient(circle at 96% 18%, rgba(38, 110, 255, 0.1), transparent 26%),
    linear-gradient(145deg, #031a30 0%, #021326 52%, #03192e 100%);
  box-sizing: border-box;
}
.feedback-page::-webkit-scrollbar { width: 7px; }
.feedback-page::-webkit-scrollbar-track { background: transparent; }
.feedback-page::-webkit-scrollbar-thumb {
  min-height: 48px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: rgba(91, 211, 235, 0.62);
  background-clip: padding-box;
}
.feedback-page::-webkit-scrollbar-thumb:hover { background-color: rgba(125, 229, 246, 0.82); }
.feedback-hero, .feedback-workspace { max-width: 1500px; margin: 0 auto; }
.feedback-hero {
  display: flex; align-items: flex-end; justify-content: space-between; gap: 32px;
  padding: 4px 6px 20px;
}
.feedback-hero h1 { margin: 8px 0 6px; color: #f1fbff; font-size: 25px; line-height: 1.2; letter-spacing: 1px; }
.feedback-hero p { max-width: 720px; margin: 0; color: #8bb4c8; font-size: 14px; line-height: 1.8; }
.hero-note {
  display: flex; align-items: center; gap: 13px; min-width: 260px; padding: 14px 18px;
  border: 1px solid rgba(72, 210, 255, 0.22); border-radius: 12px; background: rgba(4, 35, 60, 0.72);
}
.hero-note > i { color: #45d5ff; font-size: 23px; }
.hero-note div { display: flex; flex-direction: column; gap: 3px; }
.hero-note strong { color: #e9faff; font-size: 13px; }
.hero-note span { color: #739db1; font-size: 12px; }
.feedback-workspace { display: grid; grid-template-columns: minmax(440px, 0.9fr) minmax(480px, 1.1fr); gap: 20px; align-items: start; }
.panel {
  border: 1px solid rgba(77, 177, 220, 0.2); border-radius: 16px;
  background: linear-gradient(150deg, rgba(6, 38, 64, 0.94), rgba(3, 27, 49, 0.96));
  box-shadow: 0 20px 55px rgba(0, 5, 16, 0.28), inset 0 1px rgba(255, 255, 255, 0.03);
}
.submit-panel { padding: 24px 26px 26px; }
.history-panel { padding: 24px 24px 20px; min-height: 570px; }
.panel-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 22px; }
.panel-heading > div { display: flex; align-items: center; gap: 10px; }
.panel-heading h2 { margin: 0; color: #ecfaff; font-size: 18px; letter-spacing: 0.5px; }
.required-tip { color: #61899d; font-size: 12px; }
.type-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; width: 100%; }
.type-option {
  position: relative; display: flex; align-items: center; gap: 10px; min-height: 66px; padding: 10px 12px;
  color: #87adbf; text-align: left; border: 1px solid rgba(95, 164, 193, 0.22); border-radius: 10px;
  background: rgba(2, 21, 39, 0.62); cursor: pointer; transition: all 0.2s ease;
}
.type-option:hover { border-color: rgba(73, 207, 247, 0.52); background: rgba(6, 49, 76, 0.72); transform: translateY(-1px); }
.type-option.is-active { color: #deeff5; border-color: #2bc8f3; background: rgba(13, 107, 139, 0.22); box-shadow: inset 0 0 18px rgba(19, 176, 218, 0.08); }
.type-option > i:first-child { width: 24px; color: #4cd8ff; font-size: 20px; text-align: center; }
.type-option span { display: flex; flex-direction: column; gap: 3px; }
.type-option strong { color: inherit; font-size: 13px; }
.type-option small { color: #638b9e; font-size: 11px; }
.selected-icon { position: absolute; top: 7px; right: 7px; color: #42d9ff; font-size: 13px; }
.submit-guidance { display: flex; align-items: center; gap: 7px; margin: -3px 0 18px; color: #6e98aa; font-size: 12px; }
.submit-guidance i { color: #43c6eb; }
.submit-button { width: 100%; height: 44px; font-weight: 700; letter-spacing: 1px; border: none; background: linear-gradient(90deg, #078ecb, #00bddd); box-shadow: 0 8px 25px rgba(0, 172, 220, 0.2); }
.submit-button i { margin-right: 5px; }
.history-heading { margin-bottom: 14px; }
.refresh-button { color: #63dfff; border-color: rgba(82, 203, 239, 0.25); background: rgba(3, 26, 46, 0.7); }
.status-filter { margin-bottom: 16px; }
.feedback-list { min-height: 420px; }
.feedback-card {
  display: block; width: 100%; margin: 0 0 11px; padding: 16px 17px 13px; color: inherit; text-align: left;
  border: 1px solid rgba(75, 154, 188, 0.17); border-radius: 11px; background: rgba(2, 22, 41, 0.64);
  cursor: pointer; transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}
.feedback-card:hover { border-color: rgba(58, 207, 246, 0.45); background: rgba(5, 38, 62, 0.9); transform: translateX(2px); }
.card-topline, .card-footer, .detail-meta { display: flex; align-items: center; justify-content: space-between; }
.type-label { display: inline-flex; align-items: center; gap: 6px; color: #65d9f9; font-size: 12px; }
.status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px; border-radius: 20px; font-size: 11px; }
.status-badge i { width: 6px; height: 6px; border-radius: 50%; background: currentColor; box-shadow: 0 0 7px currentColor; }
.status-0 { color: #ffc96b; background: rgba(230, 162, 60, 0.1); }
.status-1 { color: #58d7ff; background: rgba(27, 175, 224, 0.1); }
.status-2 { color: #68e5af; background: rgba(45, 196, 131, 0.1); }
.feedback-card h3 { margin: 9px 0 6px; overflow: hidden; color: #e7f7fc; font-size: 14px; line-height: 1.4; text-overflow: ellipsis; white-space: nowrap; }
.feedback-card > p { display: -webkit-box; overflow: hidden; margin: 0 0 11px; color: #7fa4b7; font-size: 12px; line-height: 1.55; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.latest-reply { display: flex; align-items: flex-start; gap: 7px; margin: 8px 0 11px; padding: 8px 10px; color: #9bc5d5; font-size: 11px; line-height: 1.45; border-left: 2px solid #1ebbe4; background: rgba(12, 92, 119, 0.14); }
.latest-reply span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.card-footer { padding-top: 10px; color: #557f93; font-size: 11px; border-top: 1px solid rgba(91, 153, 180, 0.1); }
.card-footer span:last-child { color: #50ccea; }
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 410px; color: #60889a; text-align: center; }
.empty-icon { display: grid; place-items: center; width: 64px; height: 64px; margin-bottom: 15px; color: #3ebfe3; font-size: 26px; border: 1px solid rgba(65, 195, 232, 0.2); border-radius: 50%; background: rgba(4, 60, 84, 0.24); }
.empty-state strong { margin-bottom: 7px; color: #9bc1d0; font-size: 14px; }
.empty-state span { font-size: 12px; }
.feedback-pagination { padding-top: 5px; text-align: right; }
.detail-title { display: flex; align-items: center; gap: 10px; }
.detail-title span { color: #eafaff; font-size: 18px; font-weight: 700; }
.detail-title small { color: #55cfee; font-family: monospace; }
.detail-content { min-height: 220px; }
.detail-overlay {
  position: absolute; z-index: 20; inset: 0; min-height: 100%; padding: 22px 26px 28px;
  overflow-y: auto; box-sizing: border-box; border: 1px solid rgba(67, 193, 232, 0.24); border-radius: 14px;
  background: linear-gradient(150deg, rgba(7, 42, 69, 0.99), rgba(3, 27, 49, 0.995));
  box-shadow: 0 20px 60px rgba(0, 4, 14, 0.55);
}
.detail-overlay__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid rgba(83, 163, 194, 0.16); }
.detail-overlay__header button { display: grid; place-items: center; width: 34px; height: 34px; color: #75cce5; border: 1px solid rgba(80, 190, 225, 0.22); border-radius: 50%; background: rgba(1, 23, 40, 0.6); cursor: pointer; }
.detail-overlay__header button:hover { color: #fff; border-color: #38c7ec; }
.detail-slide-enter-active, .detail-slide-leave-active { transition: opacity 0.2s ease, transform 0.22s ease; }
.detail-slide-enter, .detail-slide-leave-to { opacity: 0; transform: translateY(12px); }
.detail-summary { padding: 18px; border: 1px solid rgba(71, 167, 205, 0.18); border-radius: 11px; background: rgba(2, 25, 45, 0.7); }
.detail-summary h2 { margin: 14px 0 8px; color: #ebfaff; font-size: 18px; }
.detail-summary > p { margin: 0; color: #9abcca; font-size: 13px; line-height: 1.8; white-space: pre-wrap; word-break: break-word; }
.progress-title { margin: 22px 0 16px; color: #d7f5ff; font-size: 14px; font-weight: 700; }
.timeline { padding: 0 8px 0 5px; }
.timeline-item { position: relative; display: grid; grid-template-columns: 32px 1fr; gap: 12px; min-height: 74px; }
.timeline-item:not(:last-child)::before { content: ''; position: absolute; left: 15px; top: 28px; bottom: 0; width: 1px; background: rgba(77, 172, 208, 0.25); }
.timeline-dot { z-index: 1; display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; font-size: 12px; }
.timeline-dot.submitted, .timeline-dot.resolved { color: #65e8b3; border: 1px solid rgba(71, 223, 160, 0.44); background: #073c3b; }
.timeline-dot.processing { color: #64dfff; border: 1px solid rgba(71, 202, 239, 0.44); background: #07384d; }
.timeline-dot.waiting { color: #ffc869; border: 1px solid rgba(234, 174, 77, 0.4); background: #443718; }
.timeline-item > div:last-child { padding: 4px 0 18px; }
.timeline-item strong { color: #d9f4fc; font-size: 13px; }
.timeline-item time { margin-left: 12px; color: #5b879a; font-size: 11px; }
.timeline-item p { margin: 6px 0 0; color: #769dad; font-size: 12px; line-height: 1.6; }
.timeline-item .record-reply { padding: 9px 11px; color: #a7ccd9; border-radius: 7px; background: rgba(17, 92, 117, 0.17); white-space: pre-wrap; }

.feedback-page /deep/ .el-form-item__label { padding-bottom: 7px; color: #a9cada; font-size: 13px; line-height: 22px; }
/* Element UI 2 的边框只放在 inner 上，外层明确清空，避免全局样式叠成双边框。 */
.feedback-page /deep/ .el-input,
.feedback-page /deep/ .el-textarea { border: 0 !important; background: transparent !important; box-shadow: none !important; }
.feedback-page /deep/ .el-input__inner,
.feedback-page /deep/ .el-textarea__inner {
  color: #dff6fc !important; border: 1px solid rgba(82, 161, 194, 0.38) !important;
  background: rgba(1, 18, 34, 0.78) !important; box-shadow: none !important;
}
.feedback-page /deep/ .el-input__inner:hover,
.feedback-page /deep/ .el-textarea__inner:hover { border-color: rgba(54, 198, 236, 0.7) !important; }
.feedback-page /deep/ .el-input__inner:focus,
.feedback-page /deep/ .el-textarea__inner:focus { border-color: #29bde6 !important; box-shadow: none !important; outline: 0; }
/* Element UI 2 的 input 和 textarea 字数提示节点不同，外层、内层都必须去掉默认白底。 */
.feedback-page /deep/ .el-input__count,
.feedback-page /deep/ .el-input__count-inner { color: #658da0 !important; background: transparent !important; box-shadow: none !important; }
.feedback-page /deep/ .el-loading-mask {
  border-radius: 10px;
  background: rgba(2, 22, 41, 0.82) !important;
  backdrop-filter: blur(2px);
}
.feedback-page /deep/ .el-loading-spinner .path { stroke: #55d8f3; }
.feedback-page /deep/ .el-loading-spinner .el-loading-text { color: #83dcef; }
::v-deep .el-radio-button__inner { color: #7299aa; border-color: rgba(79, 153, 185, 0.22); background: rgba(2, 20, 37, 0.58); box-shadow: none; }
::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner { color: #dffaff; border-color: #198fb5; background: #126986; box-shadow: -1px 0 0 0 #198fb5; }
::v-deep .el-pagination.is-background .btn-next, ::v-deep .el-pagination.is-background .btn-prev, ::v-deep .el-pagination.is-background .el-pager li { color: #88afbf; background: rgba(4, 31, 51, 0.9); }
::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active { color: #fff; background: #148eb4; }
.dialog-heading { display: flex; align-items: center; gap: 12px; }
.dialog-heading__icon { display: grid; place-items: center; width: 38px; height: 38px; color: #61dcff; border: 1px solid rgba(67, 205, 243, 0.3); border-radius: 10px; background: rgba(12, 99, 129, 0.2); }
.dialog-heading__icon i { font-size: 20px; }
.dialog-heading div { display: flex; flex-direction: column; gap: 3px; }
.dialog-heading strong { color: #eafaff; font-size: 18px; }
.dialog-heading small { color: #6f9daf; font-size: 11px; font-weight: 400; }
.user-feedback-dialog-wrapper { overflow: hidden; }
::v-deep .user-feedback-dialog { display: flex; flex-direction: column; max-height: 92vh; margin-bottom: 0; overflow: hidden; border: 1px solid rgba(78, 189, 229, 0.3); border-radius: 16px; background: linear-gradient(150deg, #062842, #03192e); box-shadow: 0 25px 80px rgba(0, 5, 15, 0.58); }
::v-deep .user-feedback-dialog .el-dialog__header { padding: 16px 22px !important; border-bottom: 1px solid rgba(83, 163, 194, 0.16) !important; background: rgba(2, 25, 44, 0.76) !important; }
::v-deep .user-feedback-dialog .el-dialog__body { flex: 1; min-height: 0; padding: 16px 18px 18px !important; overflow: hidden; color: #dff7ff; background: transparent !important; }
::v-deep .user-feedback-dialog .el-dialog__headerbtn { top: 25px; right: 23px; }
::v-deep .user-feedback-dialog .el-dialog__close { color: #71b6cc; }

@media (max-width: 1120px) {
  ::v-deep .user-feedback-dialog { width: calc(100% - 28px) !important; }
  .feedback-workspace { grid-template-columns: 1fr; }
  .history-panel { min-height: 520px; }
  .feedback-list { min-height: 360px; }
}
@media (max-width: 720px) {
  .feedback-page { padding: 16px 12px; }
  .feedback-hero { align-items: flex-start; flex-direction: column; }
  .feedback-hero h1 { font-size: 24px; }
  .hero-note { width: 100%; box-sizing: border-box; }
  .submit-panel, .history-panel { padding: 18px 15px; }
  .type-grid { grid-template-columns: 1fr; }
}
</style>
